import { ComponentProps, FocusEvent, KeyboardEvent, forwardRef } from 'react';

import { TextInput, styled } from '@ingrid/luterkort-react';

type Props = Omit<ComponentProps<typeof TextInput>, 'type'> & {
  decimals?: boolean;
};

/**
 * NumberInput from @ingrid/luterkort-react is currently
 * a styled wrapper-component around `NumberField` from react-aria-components.
 *
 * Because of how `NumberField` works, it may give you a NaN value on change,
 * which is far from optimal since NaN in TypeScript is a numeric value, just like any other number.
 *
 * Because of this, we've decided to use `TextInput` and remove it's appearance via CSS.
 */
export const Input = styled(TextInput, {
  /**
   * This is to remove the browser standard arrows
   * which fills no purpose when using small steps, for example for currency amount
   */
  "input[type='number'], input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
    appearance: 'none',
  },
});

export const NumberInput = forwardRef<HTMLInputElement, Props>(function NumberInput(props, ref) {
  const { decimals = true, ...rest } = props;

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (decimals) {
      return;
    }

    // Avoid preventing accessibility behaviour such as
    // focusing next field, or closing a dialog.
    if (event.key === 'Tab' || event.key === 'Esc') {
      return;
    }

    const disallowedKeys = [
      '.',
      ',',
      'e', // Preventing because of scientific notation
      'E', // Preventing because of scientific notation
      ' ', // Preventing because multiple spaces results in a dot in macOS
    ];

    if (disallowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    if (event.target.value === '0') {
      event.target.select();
    }
  }

  return <Input type="number" onFocus={handleFocus} onKeyDown={handleKeyDown} ref={ref} {...rest} />;
});
